import { useState, useMemo } from 'react';
import Modal from '@Common/Modal';
import { useAuth } from '@Context/CognitoAuthProvider';
import { Trash } from '@phosphor-icons/react';
import showToast from '@Common/toast/showToast';
import { PromptVersionControl } from '@Models/types';
import { PromptModalProps } from '@Models/types';
import { accountsAPI } from '@Lib/axios/account';

interface DeletePromptModalProps extends PromptModalProps {
  onConfirmDelete: (prompt: PromptVersionControl) => Promise<void>;
}

const DeletePromptModal = ({
  isOpen,
  onClose,
  selectedPrompt,
  onConfirmDelete,
}: DeletePromptModalProps) => {
  const { logout } = useAuth();
  const account = useMemo(() => accountsAPI(logout), [logout]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDelete = async () => {
    if (!selectedPrompt) return;

    setIsSubmitting(true);
    try {
      await account.deletePrompt(selectedPrompt);
      await onConfirmDelete(selectedPrompt);
      onClose();
      showToast.success('Prompt deleted successfully!');
    } catch (error) {
      console.error('Error deleting prompt:', error);
      showToast.error('Failed to delete prompt');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Prompt">
      <div className="flex flex-col gap-6">
        {selectedPrompt && (
          <div className="flex flex-col gap-4">
            <p className="text-gray-700">
              Are you sure you want to delete this prompt? This action cannot be
              undone.
            </p>
            <div className="rounded border bg-gray-50 p-4">
              <div className="text-sm">
                <div>
                  <strong>Version:</strong> {selectedPrompt.version}
                </div>
                {selectedPrompt.nickname && (
                  <div>
                    <strong>Nickname:</strong> {selectedPrompt.nickname}
                  </div>
                )}
                <div>
                  <strong>Created by:</strong> {selectedPrompt.created_by.name}
                </div>
                <div>
                  <strong>Created on:</strong> {selectedPrompt.date}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded bg-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-400 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isSubmitting}
            className="flex items-center gap-2 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:opacity-50"
          >
            <Trash size={16} />
            {isSubmitting ? 'Deleting...' : 'Delete Prompt'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeletePromptModal;
